import { useState } from 'react';
import { Badge } from '@/components/Badge';
import { Button } from '@/components/Button';
import { EmptyState } from '@/components/EmptyState';
import { ErrorState } from '@/components/ErrorState';
import { LoadingRegion, Skeleton } from '@/components/Skeleton';
import type { KitchenMenuItem } from './api';
import { useKitchenMenu, useSetAvailability } from './queries';
import { ConfirmAvailability } from '@/components/ConfirmAvailability';
import { Notice } from '@/components/Notice';
import { PageHeader } from '@/components/PageHeader';
import { SectionHeading } from '@/components/SectionHeading';
import { cardClasses } from '@/components/Card';
import { cn } from '@/lib/cn';

/**
 * The kitchen's sold-out switch (BR-DEC-009). Lists the branch menu without prices and sets one item's
 * availability at a time. The backend decides: every change is followed by a fresh read of the list.
 */
export function AvailabilityPanel() {
  const menu = useKitchenMenu();
  const change = useSetAvailability();
  const [confirming, setConfirming] = useState<KitchenMenuItem | null>(null);

  const items = (menu.data ?? []).flatMap((category) => category.items);
  const off = items.filter((item) => !item.is_available).length;
  const pendingId = change.isPending ? change.variables.menuItemId : null;

  const set = (item: KitchenMenuItem, isAvailable: boolean) =>
    change.mutate({ menuItemId: item.menu_item_id, name: item.name, isAvailable });

  return (
    <section aria-labelledby="availability-title" className="flex flex-col gap-4">
      <PageHeader
        id="availability-title"
        title="Menu availability"
        actions={
          menu.data && (
            <p className="text-sm text-muted" role="status" aria-live="polite">
              {off === 0 ? 'Everything is available' : `${off} of ${items.length} unavailable`}
            </p>
          )
        }
        subtitle="Turning a dish off stops new orders for it at once. Orders already placed are not changed."
      />

      {menu.isError && menu.data && (
        <Notice
          tone="warn"
          action={
            <Button variant="secondary" onClick={() => void menu.refetch()}>
              Try again
            </Button>
          }
        >
          Couldn&apos;t refresh the menu. Showing the last list received.
        </Notice>
      )}

      {menu.isPending ? (
        <LoadingRegion slowHint label="Loading the menu">
          <div className="grid gap-3 md:grid-cols-2">
            {Array.from({ length: 6 }, (_, index) => (
              <Skeleton key={index} className="h-16" />
            ))}
          </div>
        </LoadingRegion>
      ) : menu.isError && !menu.data ? (
        <ErrorState error={menu.error} onRetry={() => void menu.refetch()} />
      ) : items.length === 0 ? (
        <EmptyState title="There is nothing on the menu" />
      ) : (
        // One flowing list: category headings span the row, items fill a grid underneath, so no column
        // runs long while its neighbour sits empty.
        <div className="flex flex-col gap-5">
          {menu.data.map((category) => (
            <section
              key={category.category_id}
              aria-labelledby={`avail-${category.category_id}`}
              className="flex flex-col gap-2"
            >
              <SectionHeading id={`avail-${category.category_id}`} count={category.items.length}>
                {category.name}
              </SectionHeading>
              <ul className="grid gap-2 xl:grid-cols-2">
                {category.items.map((item) => (
                  <li
                    key={item.menu_item_id}
                    className={cn(
                      cardClasses({ padding: 'none' }),
                      'flex items-center justify-between gap-3 px-4 py-2.5',
                      !item.is_available && 'bg-surface/60',
                    )}
                  >
                    <div className="flex min-w-0 flex-col gap-1">
                      <span
                        className={cn(
                          'text-base leading-5 font-semibold break-words',
                          item.is_available ? 'text-ink' : 'text-muted',
                        )}
                      >
                        {item.name}
                      </span>
                      <span className="flex flex-wrap gap-1.5">
                        {item.is_available ? (
                          <Badge tone="success">Available</Badge>
                        ) : (
                          <Badge tone="danger">Unavailable</Badge>
                        )}
                        {item.is_special && <Badge tone="attention">Today&apos;s special</Badge>}
                      </span>
                    </div>
                    {item.is_available ? (
                      <Button
                        variant="secondary"
                        className="w-40 shrink-0"
                        loading={pendingId === item.menu_item_id}
                        disabled={pendingId !== null}
                        aria-label={`Mark ${item.name} unavailable`}
                        onClick={() => setConfirming(item)}
                      >
                        Mark unavailable
                      </Button>
                    ) : (
                      <Button
                        variant="primary"
                        className="w-40 shrink-0"
                        loading={pendingId === item.menu_item_id}
                        disabled={pendingId !== null}
                        aria-label={`Mark ${item.name} available`}
                        onClick={() => set(item, true)}
                      >
                        Mark available
                      </Button>
                    )}
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>
      )}

      {confirming && (
        <ConfirmAvailability
          itemName={confirming.name}
          onClose={() => setConfirming(null)}
          onConfirm={() => {
            const item = confirming;
            setConfirming(null);
            set(item, false);
          }}
        />
      )}
    </section>
  );
}
